import { FC } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Box, Paper } from "@mui/material";
import __ from "src/utils/Translations";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import {
  useFetchEmployee,
  useFetchEmployeePatterns,
  useFetchEmployeeRecordsCalendars,
} from "src/Pages/Company/Absences/api/useEmployee";
import useBasePath from "src/utils/hooks/useBasePath";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesEmployeeAbsenceRecordsWrite } from "src/utils/RoleBasedSecurity/permissions";
import { useFetchCalendars } from "src/Pages/Company/Absences/api/useCalendars";
import { useListAbsenceTypes } from "src/Pages/Company/Absences/api/useAbsenceTypes";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import Title from "src/Pages/Company/Absences/Title/Title";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import { Loader } from "src/Components/Loader/Loader";
import PermissionsAlert from "src/Pages/Company/Absences/PermissionsAlert";
import EmployeeCard from "src/Pages/Company/Absences/EmployeeCard";
import AbsenceRecordsCalendarsForm from "./AbsenceRecordsPatternsAndCalendarsForm";

interface IMatchParams {
  employeeId: string;
}

const AbsenceRecordsPattersAndCalendars: FC<
  RouteComponentProps<IMatchParams>
> = ({ match }) => {
  const { employeeId } = match.params;
  const companyId = useCompanyId();
  const basePath = useBasePath();
  const canWriteAbsenceRecords = usePermission(
    absencesEmployeeAbsenceRecordsWrite
  );

  const {
    data: employee,
    isError: isErrorEmployee,
    isFetching: isFetchingEmployee,
  } = useFetchEmployee(employeeId);

  const {
    data: calendars,
    isFetching: isFetchingCalendars,
    isError: isErrorCalendars,
  } = useFetchCalendars(companyId);

  const {
    data: absenceTypes,
    isFetching: isFetchingAbsenceTypes,
    isError: isErrorAbsenceTypes,
  } = useListAbsenceTypes(companyId);

  const {
    data: employeeCalendars,
    isFetching: isFetchingEmployeeCalendars,
    isError: isErrorEmployeeCalendars,
  } = useFetchEmployeeRecordsCalendars(companyId, employeeId);

  const {
    data: patternsData,
    isFetching: isFetchingPatterns,
    isError: isErrorPatterns,
  } = useFetchEmployeePatterns(employeeId);

  if (
    isFetchingEmployee ||
    isFetchingCalendars ||
    isFetchingEmployeeCalendars ||
    isFetchingAbsenceTypes ||
    isFetchingPatterns
  )
    return <Loader />;

  if (!employee) return false;

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Kartoteki pracowników"),
            to: `${basePath}/absences/absence-records`,
          },
          {
            label: __("Nieobecności pracownika"),
            to: `${basePath}/absences/employee/${employeeId}`,
          },
          {
            label: __("Edycja powiązanych kalendarzy i wzorców"),
          },
        ]}
      />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Title
          title={__("Edycja powiązanych kalendarzy i wzorców")}
          subtitle={__(
            "Przestrzeń do zarządzania wymiarami w module nieobecności na konkretnym pracowniku."
          )}
        />
      </Box>
      {!canWriteAbsenceRecords && <PermissionsAlert />}
      {isErrorEmployee ||
      isErrorCalendars ||
      isErrorEmployeeCalendars ||
      isErrorAbsenceTypes ||
      isErrorPatterns ? (
        <ErrorDataFetching />
      ) : (
        <>
          <EmployeeCard data={employee} />
          <Paper sx={{ p: "2rem" }}>
            {employeeCalendars && (
              <AbsenceRecordsCalendarsForm
                employeeId={employeeId}
                calendars={calendars}
                absenceTypes={absenceTypes}
                employeeCalendars={employeeCalendars}
                patternsData={patternsData}
              />
            )}
          </Paper>
        </>
      )}
    </>
  );
};

export default AbsenceRecordsPattersAndCalendars;
